/**
 * @fileoverview EandOForm - E&O Policy Details Form Component
 *
 * Main form component for entering/editing E&O insurance policy details.
 * Displays 6 input fields with field cascade validation and debounced auto-save.
 *
 * Form Fields (in cascade order):
 * 1. Carrier Name - Insurance carrier (e.g., "State Farm")
 * 2. Policy Number - Carrier-assigned policy number
 * 3. Per Occurrence Coverage - Coverage amount per incident ($)
 * 4. Aggregate Coverage - Total coverage amount ($)
 * 5. Effective Date - Policy start date (date picker)
 * 6. Expiration Date - Policy end date (date picker)
 *
 * Business Rules Implemented:
 * - Field Cascade: Each field enables only after previous field filled
 * - Personal Policies: All fields editable, all required
 * - Company Policies: Fields read-only for non-admins (managed by staff)
 * - Admins: Can edit all fields regardless of policy type
 * - Auto-save: 500ms debounce after field changes
 * - Date Validation: Effective date must be < Expiration date
 * - Currency Validation: Per/Agg coverage accept only numeric + decimal
 *
 * Component Integration:
 * - Embeds EandOPolicyUploader at bottom (PDF upload)
 * - Reads/writes to EopolicyStore (MobX)
 * - Calls parent onAutoSave callback on validation changes
 *
 * Related Documentation:
 * - /docs/modules/AgentEandOManager/components.md - Component details
 * - /docs/modules/AgentEandOManager/business-logic.md - Field cascade logic
 *
 * @module components/agents/AgentEandOManager/EandOForm
 * @requires react
 * @requires mobx-react-lite
 * @requires moment
 */

import React from 'react'
import { observer } from 'mobx-react-lite'
import { toast } from 'react-toastify'
import EandOPolicyUploader from './EandOPolicyUploader.component'
import EandOPolicySupplier from './EandOPolicySupplier.component'
import LoadingSpinner from './../../shared/LoadingSpinner.component'
import { MDBRow, MDBBtn, MDBCol, MDBInput } from 'mdb-react-ui-kit'
import { UIInput, UIDatePickerInput } from './../../forms/form-fields'
import moment from 'moment'
import EopolicyStore from './Eopolicy.store'
import AgentProfileStore from './../AgentProfileIntake/AgentProfile.store'
import UserProfileService from './../../../shared/services/UserProfile.service'

import './EandOForm.component.scss'

/**
 * Convert various date formats to Date object
 *
 * Handles:
 * - ISO strings with timestamp (YYYY-MM-DDTHH:MM:SS)
 * - Date strings (YYYY-MM-DD)
 * - Date objects (passthrough)
 *
 * @private
 * @param {string|Date|null} value - Date value to convert
 * @returns {Date|undefined} Date object or undefined if invalid
 *
 * @example
 * convertDate('2024-01-01T00:00:00') // Date object for 2024-01-01
 * convertDate('2024-01-01')          // Date object for 2024-01-01
 * convertDate(new Date())            // Same Date object
 */
const convertDate = value => {
  value = value && typeof value === 'string' ? value.split('T')[0] : value

  if (value && typeof value === 'string') return moment(value, 'YYYY-MM-DD').toDate()
  if (value && typeof value === 'object') return value
}

/**
 * Currency input keypress validation handler
 *
 * Restricts coverage amount fields to numeric input with max 2 decimal places.
 * Prevents non-numeric characters and enforces decimal precision rules.
 *
 * Allowed Input:
 * - Digits 0-9
 * - Decimal point (only if not already present)
 * - Max 2 digits after decimal
 *
 * Validation Rules:
 * - Only 1 decimal point allowed
 * - Max 2 decimal places (e.g., 1000000.00)
 * - No letters, symbols, or special characters
 *
 * @private
 * @param {KeyboardEvent} event - Keypress event from input field
 * @returns {boolean} True if key allowed, false if prevented
 *
 * @example
 * // In UIInput for per_coverage_amt
 * <UIInput
 *   name="per_coverage_amt"
 *   onKeyPress={currencyKeypress}
 * />
 * // User types "1000000" → Allowed
 * // User types "1000000.00" → Allowed
 * // User types "1000000.001" → 3rd decimal prevented
 * // User types "1000000..00" → 2nd decimal prevented
 */
const currencyKeypress = event => {
  const { Policy } = EopolicyStore

  let name = event.target.name,
    isNumeric = event.which >= 48 && event.which <= 57,
    isDecimal = parseInt(event.which) === 46,
    decimalExists = `${Policy.get(name)}`.indexOf('.') > -1

  if ((isNumeric || isDecimal) && !decimalExists) return true

  if (isNumeric && decimalExists && `${Policy.get(name)}`.split('.')[1].length < 2) return true

  event.preventDefault()
  return false
}

/**
 * E&O Policy Details Form Component
 *
 * Renders 6 form fields for policy details with field cascade validation.
 * Includes embedded EandOPolicyUploader for PDF certificate upload.
 *
 * Field Cascade Logic:
 * - Carrier Name: Always enabled (first field)
 * - Policy Number: Enabled when carrier_name filled
 * - Per Coverage: Enabled when carrier_name + eopolicy_num filled
 * - Agg Coverage: Enabled when per_coverage_amt filled
 * - Effective Date: Enabled when agg_coverage_amt filled
 * - Expiration Date: Enabled when effective_date filled
 *
 * Read-Only Behavior:
 * - Personal policies: All fields editable (agent input)
 * - Company policies: All fields read-only for non-admins
 * - Admins: Can edit all fields (override read-only)
 *
 * Auto-Save Behavior:
 * - Enabled when autoSave prop = true
 * - Saves 500ms after field change (debounced)
 * - Calls onAutoSave callback after each save with validation status
 *
 * Validation Display:
 * - showValidity[fieldName]: Tracks which fields show errors
 * - Red border on invalid fields
 * - Error messages below invalid fields
 * - Date validation: effective < expiration
 *
 * @component
 * @param {object} props - Component props
 * @param {boolean} [props.autoSave=false] - Enable debounced auto-save
 * @param {function} [props.onAutoSave] - Callback after save: (isValid: boolean) => void
 *
 * @returns {JSX.Element} Form fields + PDF uploader or loading spinner
 *
 * @example
 * // In AgentEandOManager
 * <EandOForm
 *   autoSave={true}
 *   onAutoSave={(isValid) => {
 *     Store.setValidity('eopolicy', isValid)
 *   }}
 * />
 *
 * @example
 * // Field cascade example (carrier_name filled)
 * <UIInput
 *   label="Carrier Name"
 *   disabled={false}  // Always enabled
 * />
 * <UIInput
 *   label="Policy Number"
 *   disabled={!Policy.get('carrier_name')}  // Enabled after carrier filled
 * />
 */
const EandOForm = props => {
  const { policyType, Policies, savePolicy, showValidity, isSaving, isLoading, getIsValid, getIsDatesValid } =
      EopolicyStore,
    Policy = Policies[policyType]
  EopolicyStore.Policy = Policy

  const isAdmin = UserProfileService.isA(['internal-staff', 'internal-admin', 'agency-owner', 'system-admin'], true),
    isSigAgent = UserProfileService.isA(['signature-higher-group']),
    isReadOnly = isAdmin ? false : policyType !== 'personal',
    isDatesValid = getIsDatesValid(),
    minDate = moment(
      Policy?.get('effective_date') && typeof Policy.get('effective_date') === 'object'
        ? Policy.get('effective_date')
        : new Date()
    )
      .add(1, 'days')
      .toDate(),
    EffectiveDate = React.forwardRef((props, ref) => <MDBInput label='Effective Date' ref={ref} {...props} />),
    ExpiryDate = React.forwardRef((props, ref) => <MDBInput label='Expiration Date' ref={ref} {...props} />),
    effective_date =
      Policy && convertDate(Policy?.get('effective_date'))
        ? moment(convertDate(Policy?.get('effective_date'))).format('MM/DD/YYYY')
        : Policy?.get('effective_date'),
    expiration_date =
      Policy && convertDate(Policy?.get('expiration_date'))
        ? moment(convertDate(Policy?.get('expiration_date'))).format('MM/DD/YYYY')
        : Policy?.get('expiration_date'),
    autoSave = !isReadOnly && props?.autoSave === true,
    onAutoSave = typeof props?.onAutoSave === 'function' ? props?.onAutoSave : () => {},
    isValid = getIsValid({ skipProps: ['eopolicy_pdf'] })

  const renderSpinner = () => {
    return (
      <div className='eo-loading-spinner'>
        <LoadingSpinner size='sm' isActive={true} label={'E&O Records'} />
      </div>
    )
  }

  const renderPolicyType = () => {
    return isSigAgent ? <EandOPolicySupplier /> : <></>
  }

  const renderForm = () => {
    if (isLoading) return renderSpinner()

    if (Policy)
      return (
        <>
          <MDBCol size='12' sm='9'>
            <MDBRow>
              {renderPolicyType()}
              <MDBCol size='12' sm='6'>
                <div
                  className={[
                    'input-wrapper input-select-wrapper',
                    !Policy?.get('carrier_name') ? 'active-input' : '',
                  ].join(' ')}
                >
                  <UIInput
                    label='E&O Policy Carrier'
                    name='carrier_name'
                    value={Policy?.get('carrier_name')}
                    disabled={isReadOnly}
                    onChange={event => {
                      EopolicyStore.Policy.set('carrier_name', event.target.value)
                      if (!EopolicyStore.Policy.get('carrier_name'))
                        return (EopolicyStore.showValidity.carrier_name = true)
                      EopolicyStore.showValidity.carrier_name = false
                      if (autoSave === true) savePolicy({ debounce: 250 }).finally(() => onAutoSave(getIsValid()))
                    }}
                    rules={{
                      required: true,
                    }}
                    required
                    showValidity={showValidity?.carrier_name}
                  />
                </div>
              </MDBCol>
              <MDBCol size='12' sm='6'>
                <div
                  className={[
                    'input-wrapper',
                    !Policy?.get('eopolicy_num') ? 'active-input' : '',
                    !Policy?.id() && !Policy?.get('carrier_name') ? 'disabled-input' : '',
                    Policy?.id() && !Policy?.get('eopolicy_num') ? 'invalid-input' : '',
                  ].join(' ')}
                >
                  <UIInput
                    label='E&O Policy Number'
                    name='eopolicy_num'
                    disabled={isReadOnly || !Policy?.get('carrier_name')}
                    value={Policy?.get('eopolicy_num')}
                    onChange={event => {
                      EopolicyStore.Policy.set('eopolicy_num', event.target.value)
                      if (!EopolicyStore.Policy.get('eopolicy_num'))
                        return (EopolicyStore.showValidity.eopolicy_num = true)
                      EopolicyStore.showValidity.eopolicy_num = false
                      if (autoSave === true) savePolicy({ debounce: 250 }).finally(() => onAutoSave(getIsValid()))
                    }}
                    rules={{
                      required: true,
                    }}
                    required
                    showValidity={showValidity?.eopolicy_num}
                  />
                </div>
              </MDBCol>
              <MDBCol size='12' sm='6'>
                <div
                  className={[
                    'input-wrapper',
                    !Policy?.get('per_coverage_amt') ? 'active-input' : '',
                    !Policy?.id() && !Policy?.get('eopolicy_num') ? 'disabled-input' : '',
                    Policy?.id() && !Policy?.get('per_coverage_amt') ? 'invalid-input' : '',
                  ].join(' ')}
                >
                  <UIInput
                    label='Coverage Amount (each occurrence)'
                    name='per_coverage_amt'
                    disabled={isReadOnly || !Policy?.get('carrier_name') || !Policy?.get('eopolicy_num')}
                    value={Policy?.get('per_coverage_amt')}
                    onChange={event => {
                      const value = event.target.value
                      EopolicyStore.Policy.set('per_coverage_amt', value)

                      if (!value || value > 999999999) return (EopolicyStore.showValidity.per_coverage_amt = true)

                      EopolicyStore.showValidity.per_coverage_amt = false
                      if (autoSave === true) savePolicy({ debounce: 250 }).finally(() => onAutoSave(getIsValid()))
                    }}
                    rules={{
                      required: true,
                      maxValue: 999999999,
                      minValue: 1,
                    }}
                    showValidity={showValidity?.per_coverage_amt}
                    onKeyPress={currencyKeypress}
                    required
                  />
                </div>
              </MDBCol>
              <MDBCol size='12' sm='6'>
                <div
                  className={[
                    'input-wrapper',
                    !Policy?.get('agg_coverage_amt') ? 'active-input' : '',
                    !Policy?.id() && !Policy?.get('per_coverage_amt') ? 'disabled-input' : '',
                    Policy?.id() && !Policy?.get('agg_coverage_amt') ? 'invalid-input' : '',
                  ].join(' ')}
                >
                  <UIInput
                    label='Coverage Amount (Aggregate)'
                    name='agg_coverage_amt'
                    disabled={
                      isReadOnly ||
                      !Policy?.get('carrier_name') ||
                      !Policy?.get('eopolicy_num') ||
                      !Policy?.get('per_coverage_amt')
                    }
                    value={Policy?.get('agg_coverage_amt')}
                    onChange={event => {
                      const value = event.target.value
                      EopolicyStore.Policy.set('agg_coverage_amt', value)

                      if (!value || value > 999999999) return (EopolicyStore.showValidity.agg_coverage_amt = true)

                      EopolicyStore.showValidity.agg_coverage_amt = false
                      if (autoSave === true) savePolicy({ debounce: 250 }).finally(() => onAutoSave(getIsValid()))
                    }}
                    rules={{
                      required: true,
                      maxValue: 999999999,
                      minValue: 1,
                    }}
                    showValidity={showValidity?.agg_coverage_amt}
                    onKeyPress={currencyKeypress}
                    required
                  />
                </div>
              </MDBCol>
              <MDBCol size='12' sm='6'>
                <div
                  className={[
                    'input-wrapper',
                    Policy?.get('eopolicy_num') &&
                    Policy?.get('carrier_name') &&
                    Policy?.get('per_coverage_amt') &&
                    Policy?.get('agg_coverage_amt') &&
                    !effective_date
                      ? 'active-input'
                      : '',
                    !Policy?.id() && !Policy?.get('agg_coverage_amt') ? 'disabled-input' : '',
                    Policy?.id() && (!effective_date || !isDatesValid) ? 'invalid-input' : '',
                  ].join(' ')}
                >
                  <UIDatePickerInput
                    showYearDropdown
                    yearDropdownItemNumber={100}
                    scrollableYearDropdown
                    disabled={
                      isReadOnly ||
                      !Policy?.get('eopolicy_num') ||
                      !Policy?.get('carrier_name') ||
                      !Policy?.get('per_coverage_amt') ||
                      !Policy?.get('agg_coverage_amt')
                    }
                    selected={effective_date ? moment(effective_date, 'MM/DD/YYYY').toDate() : null}
                    dateFormat='MM/dd/yyyy'
                    name='effective_date'
                    onChange={event => {
                      const curr = Policy.get('effective_date'),
                        change = event.target.value

                      EopolicyStore.Policy.set('effective_date', change)

                      if (
                        (curr && !change) ||
                        (!curr && change) ||
                        moment(convertDate(curr)).format('MM/DD/YYYY') !==
                          moment(convertDate(change)).format('MM/DD/YYYY')
                      ) {
                        EopolicyStore.showValidity.effective_date = false
                        if (autoSave === true) savePolicy({ debounce: 250 }).finally(() => onAutoSave(getIsValid()))
                        return true
                      }

                      return (EopolicyStore.showValidity.effective_date = true)
                    }}
                    showValidity={showValidity?.effective_date}
                    rules={{ required: true }}
                    required
                    customInput={<EffectiveDate />}
                  />
                  {!isDatesValid ? (
                    <small className='text--red'>
                      <strong>Expiration date must occur after Effective date.</strong>
                    </small>
                  ) : (
                    <></>
                  )}
                </div>
              </MDBCol>
              <MDBCol size='12' sm='6'>
                <div
                  className={[
                    'input-wrapper',
                    !expiration_date ? 'active-input' : '',
                    !Policy?.id() && !Policy?.get('effective_date') ? 'disabled-input' : '',
                    Policy?.id() && (!expiration_date || !isDatesValid) ? 'invalid-input' : '',
                  ].join(' ')}
                >
                  <UIDatePickerInput
                    showYearDropdown
                    yearDropdownItemNumber={100}
                    scrollableYearDropdown
                    disabled={
                      isReadOnly ||
                      !Policy?.get('eopolicy_num') ||
                      !Policy?.get('carrier_name') ||
                      !Policy?.get('per_coverage_amt') ||
                      !Policy?.get('agg_coverage_amt') ||
                      !Policy?.get('effective_date')
                    }
                    selected={expiration_date ? moment(expiration_date, 'MM/DD/YYYY').toDate() : null}
                    dateFormat='MM/dd/yyyy'
                    name='expiration_date'
                    onChange={event => {
                      const curr = Policy.get('expiration_date'),
                        change = event.target.value

                      EopolicyStore.Policy.set('expiration_date', change)

                      if (
                        (curr && !change) ||
                        (!curr && change) ||
                        moment(convertDate(curr)).format('MM/DD/YYYY') !==
                          moment(convertDate(change)).format('MM/DD/YYYY')
                      ) {
                        EopolicyStore.showValidity.expiration_date = false
                        if (autoSave === true) savePolicy({ debounce: 250 }).finally(() => onAutoSave(getIsValid()))
                        return true
                      }

                      return (EopolicyStore.showValidity.expiration_date = true)
                    }}
                    showValidity={showValidity?.expiration_date}
                    rules={{ required: true }}
                    required
                    minDate={minDate}
                    customInput={<ExpiryDate />}
                  />
                </div>
              </MDBCol>
              {autoSave === true || isReadOnly === true ? (
                <></>
              ) : (
                <MDBCol size='12'>
                  <MDBBtn
                    type='button'
                    size='sm'
                    block
                    color='success'
                    disabled={isReadOnly || !isValid || isSaving || isLoading}
                    className='submit-btn'
                    onClick={() => {
                      AgentProfileStore.setValidity('eopolicy', getIsValid())
                      savePolicy().then(
                        msg =>
                          toast.success('Great! The E&O policy has been updated.', {
                            position: toast.POSITION.TOP_RIGHT,
                          }),
                        err =>
                          toast.error(`${err}`, {
                            position: toast.POSITION.TOP_RIGHT,
                          })
                      )
                    }}
                  >
                    {isSaving ? (
                      <>
                        <i className='fa fa-spin fa-spinner'></i> Saving ...
                      </>
                    ) : (
                      'Save E&O Policy'
                    )}
                  </MDBBtn>
                </MDBCol>
              )}
            </MDBRow>
          </MDBCol>
          <MDBCol size='12' sm='3'>
            <EandOPolicyUploader Policy={Policy} onAutoSave={() => onAutoSave(getIsValid())} />
          </MDBCol>
        </>
      )

    if (!Policy) return renderPolicyType()
  }

  return (
    <form id='EandOFormComponent' className='container-fluid'>
      <MDBRow>{renderForm()}</MDBRow>
    </form>
  )
}

export default observer(EandOForm)
